<template>
  <main class="mx-auto mt-[64px] md:mt-[67px] md:mb-10">
    <section class="mx-auto relative video-frame">
      <UtilsSkeletonLoading v-if="isLoading" class="w-full h-[702px] mt-3" />
      <template v-else>
        <div class="overlay-btn pt-7 pb-10 absolute top-0 w-full">
          <CommonBackToButton
            class="!pt-0 container"
            :light-theme="true"
            text-to="Videos"
            url="/videos"
          />
        </div>
        <iframe
          :src="contentsData.attributes?.layout_component?.[0]?.url"
          class="mx-auto w-full h-[90vh] mt-3"
        ></iframe>
        <div
          class="overlay-scroll absolute bottom-0 w-full py-2 text-center flex justify-center items-center gap-3"
        >
          <NuxtIcon name="chevron-down" class="chevron-icon" filled />
          <span class="text-bw-400 font-medium text-sm leading-[22px]"
            >Scroll for details</span
          >
        </div>
      </template>
    </section>

    <section
      class="container mx-auto md:w-full py-8 md:pt-15 border-b border-[#CECED1] md:pb-14"
    >
      <template v-if="isLoading">
        <div class="flex flex-col gap-4">
          <UtilsSkeletonLoading class="w-1/2 h-14" />
          <UtilsSkeletonLoading class="w-full md:w-2/3 h-20" />
          <UtilsSkeletonLoading class="w-1/5 h-8" />
        </div>
        <div class="flex flex-row gap-4 mt-6">
          <UtilsSkeletonLoading class="w-14 h-10" />
          <UtilsSkeletonLoading class="w-10 h-10" />
          <UtilsSkeletonLoading class="w-10 h-10" />
          <UtilsSkeletonLoading class="w-10 h-10" />
        </div>
      </template>
      <template v-else>
        <div class="video-desc">
          <h1
            class="font-medium text-bw-900 text-lg md:text-[32px] leading-normal mb-5"
          >
            {{ contentsData.attributes?.title }}
          </h1>
          <div
            class="text-sm md:text-base text-bw-900 mb-3 w-full md:w-2/3"
            v-html="contentsData.attributes?.layout_component?.[0]?.content"
          ></div>
          <p class="text-sm md:text-base text-[#A5A5A5]">
            {{
              useFormattedDate(
                contentsData.attributes?.layout_component?.[0]?.date
              )
            }}
          </p>
        </div>

        <div class="flex flex-row gap-4 items-center mt-6">
          <span class="text-base self-center md:self-auto text-bw-900"
            >Share</span
          >
          <div class="flex flex-row gap-1">
            <button class="mr-4" @click="copyLink">
              <NuxtIcon
                name="copy-link"
                filled
                class="share-icon cursor-pointer"
              />
            </button>
            <CommonSocialShare :social-media="socMedList" :vertical="false" />
          </div>
        </div>
      </template>
    </section>

    <section
      class="related-videos container mx-auto mt-8 md:mt-10 pb-15 w-[90%] md:w-full"
    >
      <h2
        class="text-[22px] leading-normal sm:text-[40px] sm:leading-[50px] text-black-900 font-medium mb-8"
      >
        Related Videos
      </h2>
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-8 md:gap-y-14"
      >
        <template v-if="isLoading">
          <div v-for="i in 4" :key="i" class="flex flex-col gap-4">
            <UtilsSkeletonLoading class="h-44" />
            <UtilsSkeletonLoading class="h-10" />
          </div>
        </template>
        <template v-else>
          <CommonVideoCard
            v-for="item in relatedVideos"
            :key="item.id"
            :data="item"
          />
        </template>
      </div>
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const isLoading = ref(true)
const contentsData = ref([])
const relatedVideos = ref([])

const route = useRoute()

const getContents = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/contents/${route.params.slug}`,
    {
      query: {
        populate: 'image,layout_component,layout_component.type,seo.image',
        'filters[$and][0][layout][$eq]': 'video',
        sort: 'publish_at:desc'
      }
    }
  )
  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Case Studies does not exist`
    })
  }

  return data.value
}

const contents = await getContents()
contentsData.value = contents.data

const getVideos = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/contents-video-randomly`,
    {
      query: {
        populate: 'image,layout_component',
        'filters[id][$ne]': contentsData.value.id
      }
    }
  )
  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Videos does not exist`
    })
  }

  return data.value
}

const relatedVid = await getVideos()
relatedVideos.value = relatedVid.data

const currentUrl = () => {
  let locHref = ''
  if (process.client) {
    locHref = window.location.href
  }
  return locHref
}

const socMedList = ref([
  {
    name: 'whatsapp',
    title: contentsData.value.attributes?.title,
    url: currentUrl(),
    description: contentsData.value.attributes?.layout_component?.[0]?.content
  },
  {
    name: 'linkedin',
    title: contentsData.value.attributes?.title,
    url: currentUrl(),
    description: contentsData.value.attributes?.layout_component?.[0]?.content
  }
])

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: contentsData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: contentsData.value?.attributes?.url || 'suitmedia.com',
  description:
    contentsData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    contentsData.value?.attributes?.seo?.image.data?.attributes?.url ||
    contentsData?.value?.attributes?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: contentsData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: contentsData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    contentsData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    contentsData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    contentsData.value?.attributes?.seo?.image.data?.attributes?.url ||
    contentsData?.value?.attributes?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: contentsData.value?.attributes?.seo?.open_graph_type,
  twitterTitle: contentsData.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    contentsData.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    contentsData.value?.attributes?.seo?.image.data?.attributes?.url ||
    contentsData?.value?.attributes?.image?.data?.attributes?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

const copyLink = () => {
  const el = document.createElement('textarea')
  el.value = currentUrl()
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  const tooltip = document.querySelectorAll('.tooltiptext')
  Array.prototype.forEach.call(tooltip, function (element) {
    element.innerHTML = 'Copied'
  })
  setTimeout(function () {
    Array.prototype.forEach.call(tooltip, function (element) {
      element.innerHTML = 'Copy link'
    })
  }, 2000)
}
</script>

<style lang="scss" scoped>
.overlay-btn {
  display: none;
  background: linear-gradient(180deg, #080c17 0%, rgba(8, 12, 23, 0) 100%);
}
.overlay-scroll {
  display: none;
}

.video-frame {
  &:hover {
    .overlay-btn {
      display: block;
    }
    .overlay-scroll {
      display: none;
      @media screen and (min-width: 768px) {
        display: flex;
      }
    }
  }
}

:deep(.share-icon) {
  svg {
    width: 2.5rem;
    height: 2.5rem;
    transition: all 0.2s ease-in-out;

    &:hover {
      transform: scale(1.25);
    }
  }
}

:deep(.chevron-icon) {
  svg {
    width: 24px;
    height: 24px;

    path {
      fill: #ff6700 !important;
    }
  }
}
</style>
